import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import "./UserPanelPages.css";
import Navbar from "../Component/Common/Navbar";
import {
  connectRealtimeSocket,
  disconnectRealtimeSocket,
} from "../Component/ServerPackage/CompleteCPanel/utils/realtimeSocket";

/* ==============================
   API BASE HELPER
============================== */
const normalizeBase = (value) => String(value || "").trim().replace(/\/+$/, "");

const ensureApiBase = (value) => {
  const clean = normalizeBase(value);

  if (!clean) return "http://localhost:5000/api";

  return clean.endsWith("/api") ? clean : `${clean}/api`;
};

const API_BASE = ensureApiBase(
  process.env.REACT_APP_API_BASE || "http://localhost:5000"
);

/* ==============================
   AUTH KEYS
============================== */
const AUTH_TOKEN_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
];

const AUTH_STORAGE_KEYS = [
  "token",
  "authToken",
  "accessToken",
  "userToken",
  "jwtToken",
  "isLoggedIn",
  "user",
  "currentUser",
  "name",
  "userName",
  "fullName",
  "username",
  "email",
  "userEmail",
  "user_email",
  "demoUserEmail",
];

/* ==============================
   AUTH HELPERS
============================== */
const getToken = () => {
  for (const key of AUTH_TOKEN_KEYS) {
    const localValue = localStorage.getItem(key);
    const sessionValue = sessionStorage.getItem(key);

    if (localValue && String(localValue).trim()) {
      return String(localValue).trim();
    }

    if (sessionValue && String(sessionValue).trim()) {
      return String(sessionValue).trim();
    }
  }

  return "";
};

const clearAuth = () => {
  AUTH_STORAGE_KEYS.forEach((key) => {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  });

  disconnectRealtimeSocket();
  window.dispatchEvent(new Event("auth-state-changed"));
};

/* ==============================
   FORMAT HELPERS
============================== */
const formatDateTime = (value) => {
  if (!value) return "-";

  try {
    return new Date(value).toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "-";
  }
};

const normalizeNotification = (item = {}, index = 0) => ({
  id: item.id || item.notificationId || item.notification_id || `notification-${index}`,
  title: item.title || "Notification",
  message:
    item.message ||
    item.description ||
    item.desc ||
    "No details available.",
  type: item.type || item.notificationType || item.notification_type || "info",
  isRead: Boolean(item.isRead ?? item.is_read ?? false),
  createdAt:
    item.createdAt ||
    item.created_at ||
    item.time ||
    item.created ||
    "",
  createdAtText:
    item.createdAtFormatted ||
    item.created_at_formatted ||
    item.timeFormatted ||
    formatDateTime(item.createdAt || item.created_at || item.time),
});

const getNotificationIcon = (type = "") => {
  const clean = String(type || "").toLowerCase();

  if (clean.includes("success")) return "✅";
  if (clean.includes("error") || clean.includes("failed")) return "❌";
  if (clean.includes("warning")) return "⚠️";
  if (clean.includes("payment") || clean.includes("billing")) return "💳";
  if (clean.includes("domain")) return "🌐";
  if (clean.includes("hosting")) return "🚀";
  if (clean.includes("security")) return "🔐";
  if (clean.includes("wallet")) return "👛";

  return "🔔";
};

function NotificationsPage() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [realtimeStatus, setRealtimeStatus] = useState("connecting");
  const [status, setStatus] = useState({ type: "", message: "" });

  const redirectToLogin = useCallback(() => {
    clearAuth();
    navigate("/signin", { replace: true });
  }, [navigate]);

  /* ==============================
     PROTECTED FETCH
  ============================== */
  const authFetch = useCallback(
    async (endpoint, options = {}) => {
      const token = getToken();

      if (!token) {
        redirectToLogin();
        throw new Error("Session expired. Please login again.");
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        ...options,
        cache: "no-store",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          ...(options.headers || {}),
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.status === 401 || response.status === 403) {
        redirectToLogin();
        throw new Error("Session expired. Please login again.");
      }

      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Request failed.");
      }

      return data;
    },
    [redirectToLogin]
  );

  /* ==============================
     LOAD NOTIFICATIONS
  ============================== */
  const fetchNotifications = useCallback(
    async (silent = false) => {
      try {
        if (!silent) {
          setLoading(true);
        } else {
          setRefreshing(true);
        }

        setStatus({ type: "", message: "" });

        const data = await authFetch("/notifications/my-notifications");

        const safeNotifications = Array.isArray(data?.data)
          ? data.data.map(normalizeNotification)
          : Array.isArray(data?.notifications)
          ? data.notifications.map(normalizeNotification)
          : [];

        setNotifications(safeNotifications);
      } catch (err) {
        setNotifications([]);

        setStatus({
          type: "error",
          message: err.message || "Failed to fetch notifications.",
        });
      } finally {
        if (!silent) {
          setLoading(false);
        } else {
          setRefreshing(false);
        }
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  /* ==============================
     REALTIME SOCKET
  ============================== */
  useEffect(() => {
    const socket = connectRealtimeSocket();

    if (!socket) {
      setRealtimeStatus("offline");
      return undefined;
    }

    let refreshTimer = null;

    const softRefresh = () => {
      clearTimeout(refreshTimer);

      refreshTimer = setTimeout(() => {
        fetchNotifications(true);
      }, 400);
    };

    const handleConnected = () => {
      setRealtimeStatus("connected");
    };

    const handleDisconnected = () => {
      setRealtimeStatus("offline");
    };

    const handleNotificationUpdate = (payload = {}) => {
      setRealtimeStatus("connected");

      setStatus({
        type: "success",
        message: payload?.message || "Notification updated successfully.",
      });

      softRefresh();
    };

    const handleRealtimeRefresh = () => {
      setRealtimeStatus("connected");
      softRefresh();
    };

    const handleSocketAuthError = () => {
      redirectToLogin();
    };

    socket.on("connect", handleConnected);
    socket.on("disconnect", handleDisconnected);
    socket.on("socket:connected", handleConnected);

    socket.on("notification:new", handleNotificationUpdate);
    socket.on("notification:update", handleNotificationUpdate);
    socket.on("notification:status", handleNotificationUpdate);

    socket.on("payment:status", handleRealtimeRefresh);
    socket.on("domain:status", handleRealtimeRefresh);
    socket.on("hosting:status", handleRealtimeRefresh);
    socket.on("dashboard:update", handleRealtimeRefresh);
    socket.on("activity:new", handleRealtimeRefresh);
    socket.on("wallet:update", handleRealtimeRefresh);
    socket.on("wallet:status", handleRealtimeRefresh);

    socket.on("connect_error", handleSocketAuthError);

    if (socket.connected) {
      handleConnected();
    }

    return () => {
      clearTimeout(refreshTimer);

      socket.off("connect", handleConnected);
      socket.off("disconnect", handleDisconnected);
      socket.off("socket:connected", handleConnected);

      socket.off("notification:new", handleNotificationUpdate);
      socket.off("notification:update", handleNotificationUpdate);
      socket.off("notification:status", handleNotificationUpdate);

      socket.off("payment:status", handleRealtimeRefresh);
      socket.off("domain:status", handleRealtimeRefresh);
      socket.off("hosting:status", handleRealtimeRefresh);
      socket.off("dashboard:update", handleRealtimeRefresh);
      socket.off("activity:new", handleRealtimeRefresh);
      socket.off("wallet:update", handleRealtimeRefresh);
      socket.off("wallet:status", handleRealtimeRefresh);

      socket.off("connect_error", handleSocketAuthError);

      disconnectRealtimeSocket();
    };
  }, [fetchNotifications, redirectToLogin]);

  const handleRefresh = () => {
    fetchNotifications(true);
  };

  return (
    <>
      <Navbar />

      <section className="user-page-section">
        <div className="user-page-container">
          <div className="user-page-head">
            <div>
              <h1>Notifications</h1>
              <p>
                Renewals, billing updates, and account alerts for your account
                only.
              </p>

              <div className="user-realtime-row">
                <div className="user-realtime-chip">
                  <span
                    className={
                      realtimeStatus === "connected"
                        ? "user-realtime-dot online"
                        : "user-realtime-dot offline"
                    }
                  />

                  <span>
                    {realtimeStatus === "connected"
                      ? "Realtime connected"
                      : realtimeStatus === "connecting"
                      ? "Connecting realtime..."
                      : "Realtime offline"}
                  </span>
                </div>
              </div>
            </div>

            <button
              type="button"
              className="user-page-refresh-btn"
              onClick={handleRefresh}
              disabled={loading || refreshing}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {status.message ? (
            <div className={`user-page-alert ${status.type}`}>
              {status.message}
            </div>
          ) : null}

          <div className="user-page-card">
            {loading ? (
              <p className="user-page-muted">Loading notifications...</p>
            ) : notifications.length === 0 ? (
              <p className="user-page-muted">No alerts found yet.</p>
            ) : (
              <div className="vertical-list">
                {notifications.map((item) => (
                  <div
                    className={`list-row ${
                      item.isRead ? "" : "notification-unread-row"
                    }`}
                    key={item.id}
                  >
                    <div className="activity-row-left">
                      <div className="activity-icon-box">
                        {getNotificationIcon(item.type)}
                      </div>

                      <div>
                        <h3>{item.title}</h3>
                        <p>{item.message}</p>
                        <small className="list-type">{item.type}</small>
                      </div>
                    </div>

                    <span className="list-time">{item.createdAtText}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

export default NotificationsPage;